import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CoachesAPI, PlayersAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { GoldSpinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Trash2, Users, Mail, Lock, X } from "lucide-react";

export default function AdminCoaches() {
  const [coaches, setCoaches] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", name: "", clubs: [] });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [list, players] = await Promise.all([
        CoachesAPI.list(),
        PlayersAPI.list(),
      ]);
      setCoaches(list);
      const clubs = Array.from(
        new Set(players.map((p) => (p.club_name || "").trim()).filter(Boolean)),
      ).sort();
      setAllClubs(clubs);
    } catch {
      toast.error("Impossible de charger les coachs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email et mot de passe obligatoires");
      return;
    }
    if (form.clubs.length === 0) {
      toast.error("Sélectionne au moins un club pour ce coach");
      return;
    }
    setSubmitting(true);
    try {
      await CoachesAPI.create({
        email: form.email,
        password: form.password,
        name: form.name,
        assigned_clubs: form.clubs,
      });
      toast.success("Coach créé. Donne-lui ses identifiants.");
      setForm({ email: "", password: "", name: "", clubs: [] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (coach) => {
    if (!window.confirm(`Supprimer le coach ${coach.email} ?`)) return;
    try {
      await CoachesAPI.remove(coach.id);
      toast.success("Coach supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const toggleClub = (club) => {
    setForm((f) => ({
      ...f,
      clubs: f.clubs.includes(club)
        ? f.clubs.filter((c) => c !== club)
        : [...f.clubs, club],
    }));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-mobile-safe">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour Admin
        </Link>

        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
          Laser Tech Création · Espace Admin
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tight mb-2 text-gradient-gold">
          Comptes Coach
        </h1>
        <p className="text-zinc-400 font-body text-base mb-10">
          Crée des accès limités pour les coachs : ils ne pourront éditer que
          les joueurs des clubs que tu leur assignes.
        </p>

        {/* Create coach form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="metal-card rounded-xl p-5 mb-10"
          data-testid="create-coach-form"
        >
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/[0.06] border border-white/15">
              <UserPlus className="w-4 h-4 text-zinc-300" />
            </div>
            <div>
              <div className="font-display text-lg font-bold uppercase tracking-tight">
                Nouveau coach
              </div>
              <p className="text-zinc-400 font-body text-sm mt-1">
                Crée un compte avec accès limité à un ou plusieurs clubs.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-overline">Nom</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex : Jean Dupont"
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="coach-name"
              />
            </div>
            <div>
              <Label className="text-overline">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="coach@servette-fc.ch"
                className="bg-white/[0.04] border-white/10 text-white"
                data-testid="coach-email"
                required
              />
            </div>
            <div>
              <Label className="text-overline">Mot de passe</Label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="6 caractères min"
                className="bg-white/[0.04] border-white/10 text-white font-mono"
                data-testid="coach-password"
                required
              />
            </div>
          </div>

          <Label className="text-overline mb-2 block">
            Clubs autorisés ({form.clubs.length} sélectionné{form.clubs.length > 1 ? "s" : ""})
          </Label>
          {allClubs.length === 0 ? (
            <p className="text-zinc-500 text-sm font-body py-3">
              Aucun club détecté. Crée d'abord des joueurs avec un nom de club.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-4 max-h-48 overflow-y-auto pr-1">
              {allClubs.map((club) => {
                const active = form.clubs.includes(club);
                return (
                  <button
                    key={club}
                    type="button"
                    onClick={() => toggleClub(club)}
                    data-testid={`club-pick-${club.replace(/\s+/g, "-").toLowerCase()}`}
                    className="px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-bold border transition-all"
                    style={{
                      background: active
                        ? "linear-gradient(180deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.05) 100%)"
                        : "transparent",
                      borderColor: active
                        ? "rgba(212,175,55,0.55)"
                        : "rgba(255,255,255,0.1)",
                      color: active ? "#F9DF9F" : "#a1a1aa",
                      boxShadow: active
                        ? "0 0 0 1px rgba(212,175,55,0.25), 0 0 18px rgba(212,175,55,0.18)"
                        : "none",
                    }}
                  >
                    {club}
                  </button>
                );
              })}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="create-coach-btn"
            className="text-black font-bold uppercase tracking-[0.18em] text-xs h-11 px-6"
            style={{
              background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
            }}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {submitting ? "Création..." : "Créer le coach"}
          </Button>
        </motion.form>

        {/* Coaches list */}
        <div className="metal-card rounded-xl">
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
            <Users className="w-4 h-4 text-zinc-400" />
            <div className="font-display font-bold uppercase tracking-tight text-sm">
              Coachs actifs
            </div>
            <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 font-bold ml-auto">
              {coaches.length}
            </span>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center gap-3 text-zinc-500 font-body">
              <GoldSpinner size={20} />
              <span className="text-[10px] uppercase tracking-[0.3em]">Chargement</span>
            </div>
          ) : coaches.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 font-body text-sm">
              Aucun coach pour le moment. Crée le premier ci-dessus.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {coaches.map((c) => (
                <li
                  key={c.id}
                  className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`coach-row-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight">
                      {c.name || c.email.split("@")[0]}
                    </div>
                    <div className="text-xs text-zinc-500 font-body flex items-center gap-1.5 mt-0.5">
                      <Mail className="w-3 h-3" /> {c.email}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:max-w-md">
                    {c.assigned_clubs.map((club) => (
                      <span
                        key={club}
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-white/[0.06] border border-white/10 text-zinc-300"
                      >
                        {club}
                      </span>
                    ))}
                    {c.assigned_clubs.length === 0 && (
                      <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                        Aucun club assigné
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(c)}
                    data-testid={`coach-delete-${c.id}`}
                    className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 hover:text-red-300 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
